/* eslint-disable complexity */
import { ChevronSmallDown, ChevronSmallUp, type SvgIconT } from '@elliemae/ds-icons';
import React, { useCallback, useContext, useState } from 'react';
import { ControlledDateTimePickerDatatestid } from '../../../constants/index.js';
import { ControlledDateTimePickerContext } from '../../../ControlledDateTimePickerCTX.js';
import { convertToPositiveNumberIfPossible } from '../../../utils/numberHelpers.js';
import { StyledTimeBtn, StyledWheelChangeTimeBtn, StyledWheelList, StyledWheelListItem } from './Styleds.js';
import { TimeWheelContext } from './TimeWheelContext.js';

export const HoursList = (): JSX.Element => {
  const {
    currHourNum,
    currMinuteNum,
    currMeridiem,
    visibleHours,
    handlePrevHour,
    handleNextHour,
    handleTimeWheelBtnChangeHours,
    handleCurrHourOnKeyDown,
  } = useContext(TimeWheelContext);
  const {
    prevHourBtnRef,
    currHourBtnRef,
    nextHourBtnRef,
    getIsDisabledTime,
    autoFocusHourTimeWheel,
    hours,
    latestInteractionRegion,
    currFocusDescriber,
    trackFocusTimewheelCurrHour,
    getProps,
    props: { readOnly, applyAriaDisabled },
  } = useContext(ControlledDateTimePickerContext);

  const handleCurrYearRef = useCallback(
    (ButtonDomNode: HTMLDivElement) => {
      setTimeout(() => {
        currHourBtnRef.current = ButtonDomNode;
        if (latestInteractionRegion === 'timewheel' && currFocusDescriber === 'timewheel-curr-hour')
          ButtonDomNode?.focus?.();
      });
    },
    [currFocusDescriber, latestInteractionRegion, currHourBtnRef],
  );

  const [isFocusedWheel, setIsFocusedWheel] = useState(false);

  const handleWheelFocus = useCallback(() => {
    setIsFocusedWheel(true);
    trackFocusTimewheelCurrHour();
  }, [setIsFocusedWheel, trackFocusTimewheelCurrHour]);

  const handleWheelBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsFocusedWheel(false);
    },
    [setIsFocusedWheel],
  );

  const hasDisabledStyles = readOnly || applyAriaDisabled;

  const arrowColor = !hasDisabledStyles ? (['brand-primary', '700'] as SvgIconT.ColorType) : undefined;
  return (
    <StyledWheelList onFocus={handleWheelFocus} onBlur={handleWheelBlur}>
      <StyledWheelListItem>
        <StyledWheelChangeTimeBtn
          aria-label="subtract one hour"
          buttonType="raw"
          size="m"
          onClick={handlePrevHour}
          innerRef={prevHourBtnRef}
          data-testid={ControlledDateTimePickerDatatestid.TIMEWHEEL.PREV_HOUR}
          applyAriaDisabled={applyAriaDisabled}
          readOnly={readOnly}
          getOwnerProps={getProps}
          tabIndex={-1}
          type="button"
        >
          <ChevronSmallUp color={arrowColor} />
        </StyledWheelChangeTimeBtn>
      </StyledWheelListItem>
      {visibleHours.map((hourString) => {
        const key = `timewheel-hour-${hourString || ''}`;
        if (!hourString) return <StyledWheelListItem key={key} />;
        const currHourTimeString = `${hourString}:${currMinuteNum} ${currMeridiem}`;

        const isEmptyCurrValueCurrentListItem = !hours && convertToPositiveNumberIfPossible(hourString) === currHourNum;
        const isTheItemVisuallyInTheCenter = isEmptyCurrValueCurrentListItem || hours === hourString;
        const isNotAValidSelection = getIsDisabledTime(currHourTimeString);
        const tabIndex = isTheItemVisuallyInTheCenter ? 0 : -1;
        const isSelected = hours.length === 2 && convertToPositiveNumberIfPossible(hourString) === currHourNum;
        const btnProps = {
          role: 'spinbutton',
          ...(isSelected && {
            'aria-valuenow': convertToPositiveNumberIfPossible(hourString),
            'aria-valuetext': `${hourString} hours`,
          }),
          'aria-valuemin': 1,
          'aria-valuemax': 12,
          'aria-label': `${hourString} hours`,
          'aria-disabled': readOnly || applyAriaDisabled,
          'aria-invalid': isNotAValidSelection,
          'aria-readonly': readOnly || applyAriaDisabled,
          onKeyDown: isTheItemVisuallyInTheCenter ? handleCurrHourOnKeyDown : undefined,
          innerRef: isTheItemVisuallyInTheCenter ? handleCurrYearRef : undefined,
        };
        return (
          <StyledWheelListItem key={key}>
            <StyledTimeBtn
              $isTheItemVisuallyInTheCenter={isTheItemVisuallyInTheCenter}
              $isNotAValidSelection={isNotAValidSelection}
              $selected={isSelected}
              $isUnset={!hours}
              $readOnly={readOnly || applyAriaDisabled}
              $isFocusedWheel={isFocusedWheel}
              autoFocus={autoFocusHourTimeWheel && isTheItemVisuallyInTheCenter}
              data-testid={
                ControlledDateTimePickerDatatestid.TIMEWHEEL[isTheItemVisuallyInTheCenter ? 'CURRENT_HOUR' : 'HOUR']
              }
              onClick={(e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) =>
                handleTimeWheelBtnChangeHours(hourString, e)
              }
              tabIndex={tabIndex}
              {...btnProps}
              getOwnerProps={getProps}
            >
              {hourString}
            </StyledTimeBtn>
          </StyledWheelListItem>
        );
      })}
      <StyledWheelListItem>
        <StyledWheelChangeTimeBtn
          aria-label="add one hour"
          buttonType="raw"
          size="m"
          onClick={handleNextHour}
          innerRef={nextHourBtnRef}
          tabIndex={-1}
          data-testid={ControlledDateTimePickerDatatestid.TIMEWHEEL.NEXT_HOUR}
          applyAriaDisabled={applyAriaDisabled}
          readOnly={readOnly}
          getOwnerProps={getProps}
          type="button"
        >
          <ChevronSmallDown color={arrowColor} />
        </StyledWheelChangeTimeBtn>
      </StyledWheelListItem>
    </StyledWheelList>
  );
};
